"use client";
import useEmblaCarousel from "embla-carousel-react";
import useIsRtl from "@/hooks/useIsRtl";
import { TEmblaSliderContextValue } from "./types";
import EmblaSliderContext from "./EmblaSliderContext";
import EmblaSliderContainer from "./EmblaSliderContainer";
import EmblaSliderPrevNextButtons from "./EmblaSliderPrevNextButtons";
import EmblaSliderViewport from "./EmblaSilderViewpoer";
import Autoplay from "embla-carousel-autoplay";
// import useAutoPlaySliderEffect from "./useAutoPlaySlider";
import EmblaFilters from "./EmblaFilters";
import useEmblaSliderFeatures from "./useEmblaSliderFeatures";
import useDeviceCapability from "@/hooks/useDeviceCapability";
import { watchDragOutsideNestedScroller } from "./nestedScroller";
import useStopAutoplayOnNestedScroll from "./useStopAutoplayOnNestedScroll";

type TEmblaSettings = {
  autoPlay?: boolean;
  autoplayDuration?: number;
  setisLastSlide?: (isLastSlide: boolean) => void;
  selectedIndex?: number;
  setSelectedIndex?: (slide: number) => void;
  direction?: "vertical" | "horizantal";
};

type TProps = { children: React.ReactElement } & TEmblaSettings;

type EmblaSliderComponent = React.FC<TProps> & {
  EmblaSliderContainer: typeof EmblaSliderContainer;
  EmblaSliderPrevNextButtons: typeof EmblaSliderPrevNextButtons;
  EmblaSliderViewport: typeof EmblaSliderViewport;
  EmblaFilters: typeof EmblaFilters;
};

const EmblaSlider: EmblaSliderComponent = ({
  children,
  autoPlay = false,
  autoplayDuration = 5000,
  setisLastSlide,
  selectedIndex,
  setSelectedIndex,
  direction = "horizantal",
}) => {
  // Don't run a continuous rAF autoplay loop on constrained devices (a home page can
  // have 3-4 carousels = 3-4 loops competing for a weak CPU). Falls back to the safe
  // default after mount, so capable devices behave exactly as before.
  const { isLowEnd } = useDeviceCapability();
  const autoPlayEffective = autoPlay && !isLowEnd;
  const buildPlugins = () => {
    let plugins: any[] = [];
    if (autoPlayEffective) {
      plugins = [
        ...plugins,
        Autoplay({
          delay: autoplayDuration,
          stopOnMouseEnter: true,
          stopOnInteraction: false,
        }),
      ];
    }
    return plugins;
  };
  const isRtl = useIsRtl();
  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      direction: isRtl && direction === "horizantal" ? "rtl" : "ltr",
      axis: direction === "vertical" ? "y" : "x",
      // loop: autoPlay,
      skipSnaps: true,
      // A nested native scroller — the product-card image slider — owns its own
      // horizontal gesture. Without this Embla claims the first touchmove and the
      // nested track cannot scroll on mobile at all. Inert for every other carousel:
      // the attribute only exists on a product card that has more than one image.
      // See ./nestedScroller for the full reasoning.
      watchDrag: watchDragOutsideNestedScroller,
    },
    buildPlugins(),
  );
  const contextValue: TEmblaSliderContextValue = [emblaRef, emblaApi];
  useEmblaSliderFeatures(
    emblaApi,
    selectedIndex,
    setSelectedIndex,
    setisLastSlide,
  );
  // The rail must not auto-advance while a shopper is swiping a product card's images,
  // and must come back to life once they are done. `stopOnMouseEnter` only covers PC;
  // this is the touch equivalent of that pause-and-resume pair.
  useStopAutoplayOnNestedScroll(emblaApi, autoPlayEffective);
  // useAutoPlaySliderEffect(autoPlay, emblaApi);
  return (
    <EmblaSliderContext.Provider value={contextValue}>
      {children}
    </EmblaSliderContext.Provider>
  );
};

EmblaSlider.EmblaSliderContainer = EmblaSliderContainer;
EmblaSlider.EmblaSliderPrevNextButtons = EmblaSliderPrevNextButtons;
EmblaSlider.EmblaSliderViewport = EmblaSliderViewport;
EmblaSlider.EmblaFilters = EmblaFilters;
export default EmblaSlider;
